import React, { useContext, useEffect, useState } from 'react';

import cx from 'classnames';
import gql from 'graphql-tag';
import { useTranslation } from 'react-i18next';
import { useChannelAdminContext } from 'hooks';

import { useQuery } from '@apollo/client';

import { getClient } from 'lane-shared/apollo';
import { UserDataContext, ValidationErrorContext } from 'lane-shared/contexts';
import FullWebhookFragment from 'lane-shared/graphql/fragments/FullWebhookFragment';
import PublicUserFragment from 'lane-shared/graphql/fragments/PublicUserFragment';
import { pause } from 'async-timing';
import WebhookTypeDefinition, {
  WebhookStatusEnum,
  WebhookType,
  WEBHOOK_STATUS_LABELS,
} from 'lane-shared/helpers/webhooks/WebhookType';
import { useUpdatedData, useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';

import Property from 'components/builder/properties/input/Property';
import Button from 'components/general/Button';
import ControlMenu from 'components/general/ControlMenu';
import ErrorMessage from 'components/general/ErrorMessage';
import updateWebhookMutation from 'components/webhooks/updateWebhookMutation';
import { useDuplicateUrlCheck } from 'components/webhooks/useDuplicateUrlCheck';
import { useWebhookProperties } from 'components/webhooks/useWebhookProperties';

import styles from './WebhookCreate.scss';

type Props = {
  className?: string;
  style?: React.CSSProperties;
  webhookId: string;
};

export default function WebhookEdit({ className, style, webhookId }: Props) {
  const { user } = useContext(UserDataContext);
  const { channel } = useChannelAdminContext();
  const { t } = useTranslation();
  const isReservationWebhookEnabled = useFlag(
    FeatureFlag.ContentReservationEventWebhook,
    false
  );
  const properties = useWebhookProperties();
  const [webhook, setWebhook, hasChanges, getPatch] =
    useUpdatedData<WebhookType | null>(null);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<Error | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const webhookQuery = useQuery(
    gql`
      ${PublicUserFragment}
      ${FullWebhookFragment}
      query getWebhook($webhookId: UUID!) {
        webhook(_id: $webhookId) {
          ...FullWebhookFragment
        }
      }
    `,
    {
      skip: !webhookId,
      variables: {
        webhookId,
      },
    }
  );

  useEffect(() => {
    if (webhookQuery.data?.webhook) {
      setWebhook(webhookQuery.data.webhook, true);
    }
  }, [webhookQuery.data?.webhook]);

  const { showActiveError, activeErrorText, showInactiveWarning, inactiveWarningText } =
    useDuplicateUrlCheck({
      channelId: channel?._id,
      currentUrl: webhook?.url ?? '',
      intendedStatus: webhook?.status ?? WebhookStatusEnum.Live,
      excludeWebhookId: webhookId,
      isEnabled: !!isReservationWebhookEnabled,
    });

  function updateWebhook(update: Partial<WebhookType>) {
    setWebhook(update);
  }

  async function saveWebhook() {
    const validator = WebhookTypeDefinition.buildSchema(
      !!isReservationWebhookEnabled
    );

    const webhookData = {
      ...webhook,
      ...getPatch(),
    };

    try {
      await validator.validate(webhookData, { abortEarly: false });
      setValidation(null);
    } catch (err) {
      setValidation(err);

      return;
    }

    try {
      setLoading(true);
      setError(null);

      await pause();
      await getClient().mutate({
        mutation: updateWebhookMutation,
        variables: {
          webhook: {
            _id: webhookId,
            ...getPatch(),
          },
        },
      });

      window.Toast.show('Webhook updated!');
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={cx(styles.WebhookCreate, className)} style={style}>
      <div className={styles.pageHeader}>
        <p className={styles.helperText}>
          {t('web.admin.webhooks.helperText')}
        </p>
        <ControlMenu>
          <hr />
          <Button
            loading={loading}
            disabled={!hasChanges || showActiveError}
            variant="contained"
            onClick={saveWebhook}
          >
            Update
          </Button>
        </ControlMenu>
      </div>
      <ErrorMessage error={error} />
      {!isReservationWebhookEnabled && <ErrorMessage error={validation} />}
      <ValidationErrorContext.Provider
        value={isReservationWebhookEnabled ? (validation as any) : null}
      >
      {webhook &&
        Object.entries(properties).map(([key, property]) =>
          property.hidden ? null : (
            <div key={key} className={styles.property}>
              <Property
                object={webhook}
                // @ts-expect-error ts-migrate(2322) FIXME: Type 'ChannelType | null' is not assignable to typ... Remove this comment to see the full error message
                channel={channel}
                label={property.friendlyName || property.name || key}
                user={user}
                property={property}
                propertyKey={key}
                value={
                  key === 'status' &&
                  isReservationWebhookEnabled &&
                  webhook[key]
                    ? {
                        _id: webhook[key],
                        name:
                          WEBHOOK_STATUS_LABELS[webhook[key] as string] ||
                          webhook[key],
                      }
                    : // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                      webhook[key]
                }
                onChange={value => {
                  // status uses option objects when FF is on; extract _id
                  const resolved =
                    key === 'status' && value?._id ? value._id : value;

                  updateWebhook({ [key]: resolved });
                }}
                onPropertyChange={({ key, value }) =>
                  updateWebhook({ [key]: value })
                }
              />
              {key === 'url' && showActiveError && (
                <p className={styles.activeDuplicateError}>
                  {activeErrorText}
                </p>
              )}
              {key === 'url' && showInactiveWarning && (
                <p className={styles.inactiveDuplicateWarning}>
                  {inactiveWarningText}
                </p>
              )}
            </div>
          )
        )}
        </ValidationErrorContext.Provider>
    </div>
  );
}
